import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DynamicReviewsExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { IntegrationsMap } from '@services/integrations/integration.model';

export type ReviewImportMethod = 'csv' | 'integration';

export interface ReviewPlatform {
  name: string;
  displayName: string;
  /** null until PDQ ships a provider for it — the tile renders as "Soon". */
  reviewDataSource: DynamicReviewsExtension.ReviewDataSource | null;
}

/**
 * Import-method choice plus the review platform grid, shared by Dynamic Reviews and the checkout
 * upsell so a newly released platform only has to be added once.
 *
 * Expects a form group holding `importMethod` and `reviewDataSource`. The two are mutually
 * exclusive by construction: CSV forces `reviewDataSource` to CSV, and only one platform can be
 * selected at a time.
 */
@Component({
  selector: 'pdq-review-platform-picker',
  templateUrl: './review-platform-picker.component.html',
  styleUrls: ['./review-platform-picker.component.scss'],
  standalone: false,
})
export class ReviewPlatformPickerComponent implements OnInit {
  @Input() formGroup: FormGroup;

  readonly reviewPlatforms: ReviewPlatform[] = [
    { name: 'okendo', displayName: 'Okendo', reviewDataSource: DynamicReviewsExtension.ReviewDataSource.OKENDO },
    { name: 'yotpo', displayName: 'Yotpo', reviewDataSource: DynamicReviewsExtension.ReviewDataSource.YOTPO },
    { name: 'judge_me', displayName: 'Judge.me', reviewDataSource: DynamicReviewsExtension.ReviewDataSource.JUDGE_ME },
    {
      name: 'bazaarvoice',
      displayName: 'Bazaarvoice',
      reviewDataSource: DynamicReviewsExtension.ReviewDataSource.BAZAARVOICE,
    },
    { name: 'reviews_io', displayName: 'REVIEWS.io', reviewDataSource: null },
    { name: 'trustpilot', displayName: 'Trustpilot', reviewDataSource: null },
  ];

  private integrationsMap: IntegrationsMap = {};
  private destroyRef = inject(DestroyRef);

  constructor(
    private integrationsService: IntegrationsService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.integrationsService.integrations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((integrations) => {
      this.integrationsMap = integrations ?? {};
    });
  }

  get isCsvSelected(): boolean {
    return this.formGroup.get('importMethod')?.value === 'csv';
  }

  get isIntegrationSelected(): boolean {
    return this.formGroup.get('importMethod')?.value === 'integration';
  }

  get selectedPlatform(): string | null {
    const source = this.formGroup.get('reviewDataSource')?.value;
    if (!source || source === DynamicReviewsExtension.ReviewDataSource.CSV) {
      return null;
    }
    return this.reviewPlatforms.find((platform) => platform.reviewDataSource === source)?.name ?? null;
  }

  get hasNoIntegrationSelectedError(): boolean {
    // The host's cross-field validator owns the error; fall back to the local check when it has none.
    return this.formGroup.hasError('noIntegrationSelected') || (this.isIntegrationSelected && !this.selectedPlatform);
  }

  isIntegrationConnected(platformName: string): boolean {
    return this.integrationsMap?.[platformName]?.instance?.isConnected ?? false;
  }

  isPlatformAvailable(platform: ReviewPlatform): boolean {
    return platform.reviewDataSource !== null;
  }

  isPlatformSelectable(platform: ReviewPlatform): boolean {
    return this.isPlatformAvailable(platform) && this.isIntegrationConnected(platform.name);
  }

  selectImportMethod(method: ReviewImportMethod): void {
    this.formGroup.get('importMethod')?.setValue(method);

    if (method === 'csv') {
      this.formGroup.get('reviewDataSource')?.setValue(DynamicReviewsExtension.ReviewDataSource.CSV);
      return;
    }

    const connectedPlatform = this.reviewPlatforms.find((platform) => this.isPlatformSelectable(platform));
    this.formGroup.get('reviewDataSource')?.setValue(connectedPlatform?.reviewDataSource ?? null);
  }

  selectPlatform(platform: ReviewPlatform): void {
    if (!this.isPlatformSelectable(platform)) return;
    this.formGroup.get('reviewDataSource')?.setValue(platform.reviewDataSource);
  }

  navigateToIntegrations(): void {
    this.router.navigate(['/settings/integrations']);
  }
}
